import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { IncomeFrequency } from './useIncomes';
import { useLocalStorage } from '@mantine/hooks';
import { api } from 'src/common/utils/api';
import {
  Frequency,
  useTotalForFrequency,
  calculateAnnualAmount,
  calculateMonthlyAmount,
} from '../utils';
import { Dispatch, SetStateAction } from 'react';

export type ExpenditureCategory =
  | 'Insurance'
  | 'Cable'
  | 'Online'
  | 'Service'
  | 'Other bills'
  | 'Telephone'
  | 'Utilities'
  | 'Office supplies'
  | 'Advertising'
  | 'Business'
  | 'Postage'
  | 'Printing'
  | 'Office maintenance'
  | 'Wages'
  | 'ATM'
  | 'Entertainment'
  | 'Restaurants'
  | 'Hobbies'
  | 'Dues'
  | 'Gambling'
  | 'Charitable'
  | 'Gifts'
  | 'Groceries'
  | 'General'
  | 'Healthcare'
  | 'Personal'
  | 'Child'
  | 'Clothing'
  | 'Education'
  | 'Home Maintenance'
  | 'Home Improvement'
  | 'Pets'
  | 'Electronic'
  | 'Mixed'
  | 'Uncategorized'
  | 'Other'
  | 'Cheques'
  | 'Rent'
  | 'Loans'
  | 'Mortgages'
  | 'Taxes'
  | 'Automotive'
  | 'Gasoline'
  | 'Travel';

type ApiExpenditure = {
  id: string;
  description: string;
  category: ExpenditureCategory;
  amount: number;
  frequency: IncomeFrequency;
  clientId: string;
};

export type Expenditure = ApiExpenditure & {
  amountAnnually: number;
  amountMonthly: number;
};

export type EditableExpenditure = Omit<ApiExpenditure, 'id'> & {
  id?: string;
};

type UseExpendituresReturnType = {
  isLoading: boolean;
  expendituresData: Expenditure[] | undefined;
  upsertExpenditures: (items: EditableExpenditure[]) => Promise<void>;
  deleteExpenditures: (ids: string[]) => Promise<void>;
  frequency: Frequency;
  setFrequency: Dispatch<SetStateAction<Frequency>>;
  total: number;
};

export default function useExpenditures(): UseExpendituresReturnType {
  const { clientId } = useCurrentClient();
  const queryClient = useQueryClient();
  const [frequency, setFrequency] = useLocalStorage<Frequency>({
    key: 'cashflow-expenditures-frequency',
    defaultValue: 'monthly',
  });

  const queryKey = ['clients', clientId, 'expenditures'];

  const { data, isLoading } = useQuery<ApiExpenditure[]>({
    queryKey,
    enabled: !!clientId,
  });

  const expendituresData = data?.map(expenditure => ({
    ...expenditure,
    amountAnnually: calculateAnnualAmount(expenditure.amount, expenditure.frequency),
    amountMonthly: calculateMonthlyAmount(expenditure.amount, expenditure.frequency),
  }));

  const { mutateAsync: upsertMutation } = useMutation({
    mutationFn: (items: EditableExpenditure[]) =>
      api.put(
        `clients/${clientId}/expenditures/batch`,
        items.map(item => ({ ...item, clientId })),
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const upsertExpenditures = async (items: EditableExpenditure[]) => {
    await upsertMutation(items);
  };

  const { mutateAsync: deleteMutation } = useMutation({
    mutationFn: (ids: string[]) => api.post(`clients/${clientId}/expenditures/delete-request`, ids),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteExpenditures = async (ids: string[]) => {
    await deleteMutation(ids);
  };

  const total = useTotalForFrequency({
    items: expendituresData,
    frequency,
    frequencyField: 'frequency',
    amountField: 'amount',
  });

  return {
    isLoading,
    expendituresData,
    upsertExpenditures,
    deleteExpenditures,
    frequency,
    setFrequency,
    total,
  };
}
